import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LEVEL_DATA } from '../data/gameData';
import { PlayerSprite, BerberRugBorder } from './Sprites';
import { subscribeCustomWords, getCustomWords } from '../utils/customVocabularyStorage';
import { subscribeWordOverrides } from '../utils/wordOverridesStorage';
import { getChallengeWords } from '../utils/challengeWords';
import { generateCrosswordGridsForChallenge, MAX_WORDS_PER_GRID } from '../utils/crosswordGenerator';
import { CrosswordGrid } from '../data/crosswordData';
import { playSound } from '../audio';
import { ScrollDualScreenReader } from './ScrollDualScreenReader';
import { SMS_DISCUSSIONS, SMSDiscussion } from '../data/smsDiscussionsData';
import { DAILY_DIALOGUES_2026, DailyDialogue } from '../data/dialoguesData';
import { SMSChatView } from './SMSChatView';
import {
  getActiveSmsSession,
  setActiveSmsSession,
  getActiveScrollSession,
  setActiveScrollSession,
  ActiveSmsSession,
  ActiveScrollSession,
} from '../utils/activeActivityManager';

interface InventoryViewProps {
  score: number;
  setScore: React.Dispatch<React.SetStateAction<number>>;
  unlockedSkins: string[];
  setUnlockedSkins: React.Dispatch<React.SetStateAction<string[]>>;
  currentSkin: string;
  setCurrentSkin: React.Dispatch<React.SetStateAction<string>>;
  onBack: () => void;
  unlockedLevels: number[];
  initialTab?: 'skins' | 'scrolls' | 'dialogues' | 'sms';
  onStartCelestialChallenge?: () => void;
  isAdmin?: boolean;
}

interface SkinItem {
  id: string;
  name: string;
  tier: string;
  stageNum: number;
  cost: number;
  desc: string;
  badge: string;
}

const SKINS: SkinItem[] = [
  { 
    id: 'default', 
    name: 'Aqcic (Héros)', 
    tier: 'Initié', 
    stageNum: 0, 
    cost: 0, 
    desc: "L'enfant de la région, gardien de la mémoire kabyle.",
    badge: '🌱 Départ'
  },
  { 
    id: 'uccen', 
    name: 'Uccen (Chacal)', 
    tier: 'Étape 1 • Bronze', 
    stageNum: 1, 
    cost: 1500, 
    desc: "Rapide et rusé, l'animal mythique de nos contes traditionnels.",
    badge: '🥉 1 500 XP'
  },
  { 
    id: 'teryel', 
    name: 'Teryel (Ogresse)', 
    tier: 'Étape 2 • Argent', 
    stageNum: 2, 
    cost: 4500, 
    desc: "Créature magique et imposante des légendes anciennes.",
    badge: '🥈 4 500 XP'
  },
  { 
    id: 'ayyis', 
    name: 'Ayyis (Cheval)', 
    tier: 'Étape 3 • Or', 
    stageNum: 3, 
    cost: 10000, 
    desc: "Fier destrier des hautes crêtes du Djurdjura.",
    badge: '🥇 10 000 XP'
  },
  { 
    id: 'igider', 
    name: 'Igider (Aigle)', 
    tier: 'Étape 4 • Légendaire', 
    stageNum: 4, 
    cost: 22000, 
    desc: "Le seigneur suprême des cieux kabyles. Réserve ultime des grands maîtres.",
    badge: '👑 22 000 XP'
  }
];

interface ChallengeItem {
  id: string;
  category: string;
  title: string;
  icon: string;
  region: string;
  requiredLevel: number;
  npcName: string;
  desc: string;
}

const CHALLENGES_LIST: ChallengeItem[] = [
  // Niveau 0 - Taddart
  {
    id: 'animaux',
    category: 'Animaux',
    title: 'Animaux du Village',
    icon: '🐾',
    region: 'Taddart',
    requiredLevel: 0,
    npcName: 'Dda Sliman (Le Fermier)',
    desc: 'Bêtes du troupeau, oiseaux et faune des montagnes kabyles.'
  },
  {
    id: 'maison',
    category: 'La Maison',
    title: 'La Maison Ancestrale',
    icon: '🏠',
    region: 'Taddart',
    requiredLevel: 0,
    npcName: 'Dda Muḥand (L\'Artisan)',
    desc: 'Objets du quotidien, pièces et foyer familial (Axxam).'
  },
  {
    id: 'famille',
    category: 'La Famille',
    title: 'La Famille & Liens',
    icon: '👨‍👩‍👧',
    region: 'Taddart',
    requiredLevel: 0,
    npcName: 'La Tajmaɛt',
    desc: 'Membres de la famille, proches et termes affectueux.'
  },
  {
    id: 'salutations',
    category: 'Salutations & Politesse',
    title: 'Salutations & Politesse',
    icon: '👋',
    region: 'Taddart',
    requiredLevel: 0,
    npcName: 'Na Nanna (L\'Aînée)',
    desc: 'Formules de politesse, souhaits et accueil (Azul, Tanemmirt, Ansuf...).'
  },
  {
    id: 'pronoms',
    category: 'Pronoms & Personnes',
    title: 'Pronoms & Personnes',
    icon: '👤',
    region: 'Taddart',
    requiredLevel: 0,
    npcName: 'Amɣar n Taddart',
    desc: 'Pronoms personnels et identités (D nekk, D kečč, D netta...).'
  },
  {
    id: 'lieux',
    category: 'Lieux & Espace',
    title: 'Lieux & Géographie',
    icon: '🗺️',
    region: 'Tamurt',
    requiredLevel: 0,
    npcName: 'Les Anciens',
    desc: 'Villes de Kabylie, orientations et repères dans l\'espace.'
  },

  // Niveau 1 - Azemmur
  {
    id: 'nature',
    category: 'Nature & Météo',
    title: 'Nature & Éléments',
    icon: '🌿',
    region: 'Azemmur',
    requiredLevel: 1,
    npcName: 'Ɛemmi Ɛli (L\'Agriculteur)',
    desc: 'Arbres, oliviers, rivières, météo et ciel.'
  },
  {
    id: 'nombres',
    category: 'Nombres & Quantité',
    title: 'Nombres & Mesures',
    icon: '🔢',
    region: 'Azemmur',
    requiredLevel: 1,
    npcName: 'Amesnuz (Le Marchand)',
    desc: 'Chiffres, comptage et mesures pour le commerce.'
  },
  {
    id: 'nourriture',
    category: 'Nourriture',
    title: 'Nourriture & Saveurs',
    icon: '🍯',
    region: 'Azemmur',
    requiredLevel: 1,
    npcName: 'Tajmaɛt n Uzemmur',
    desc: 'Plats kabyles, fruits, récoltes et saveurs traditionnelles.'
  },
  {
    id: 'questions',
    category: 'Questions & Réponses',
    title: 'Questions & Réponses',
    icon: '❓',
    region: 'Azemmur',
    requiredLevel: 1,
    npcName: 'Inebgi (Le Voyageur)',
    desc: 'Questions indispensables et réponses courantes (Amek, Anda, Ih, Ala...).'
  },
  {
    id: 'phrases',
    category: 'Phrases & Quotidien',
    title: 'Phrases & Quotidien',
    icon: '💬',
    region: 'Azemmur',
    requiredLevel: 1,
    npcName: 'Yidir (Le Jeune)',
    desc: 'Phrases complètes, échanges et expressions de la vie quotidienne.'
  },

  // Niveau 2 - Djurdjura
  {
    id: 'corps',
    category: 'Le Corps & Soins',
    title: 'Le Corps & Soins',
    icon: '🩺',
    region: 'Djurdjura',
    requiredLevel: 2,
    npcName: 'Aṭbib (Le Guérisseur)',
    desc: 'Anatomie, santé, hygiène et sensations physiques.'
  },
  {
    id: 'actions',
    category: 'Verbes',
    title: 'Verbes',
    icon: '🏃',
    region: 'Djurdjura',
    requiredLevel: 2,
    npcName: 'Aṣeggaḍ (Le Chasseur)',
    desc: 'Mouvements concrets, impératifs et verbes dynamiques du quotidien.'
  },
  {
    id: 'verbes_passe',
    category: 'Verbes au Passé',
    title: 'Verbes au Passé',
    icon: '🕰️',
    region: 'Djurdjura',
    requiredLevel: 2,
    npcName: 'Amezzuɣ (Le Gardien)',
    desc: 'Actions accomplies et conjugaisons au prétérit (Ččiɣ, Ruḥeɣ, Xedmeɣ...).'
  },
  {
    id: 'verbes_futur',
    category: 'Verbes au Futur & Présent',
    title: 'Verbes au Futur & Présent',
    icon: '⚡',
    region: 'Djurdjura',
    requiredLevel: 2,
    npcName: 'Anelmad (L\'Éclaireur)',
    desc: 'Projets, intentions et verbes avec particule (Ad ččeɣ, Ad nruḥ...).'
  },
  {
    id: 'pensee_comm',
    category: 'Pensée & Communication',
    title: 'Pensée & Communication',
    icon: '🗣️',
    region: 'Djurdjura',
    requiredLevel: 2,
    npcName: 'Bab n Wawal',
    desc: 'Écoute, parole, réflexion et certitudes (Mmeslay, Fhemeɣ, Ẓriɣ...).'
  },
  {
    id: 'transports',
    category: 'Jeux & Transports',
    title: 'Jeux & Voyages',
    icon: '🚗',
    region: 'Djurdjura',
    requiredLevel: 2,
    npcName: 'Les Voyageurs',
    desc: 'Jeux d\'enfants, véhicules et moyens de transport.'
  },

  // Niveau 3 - Iɣil
  {
    id: 'emotions',
    category: 'Émotions & Sensations',
    title: 'Émotions & Sentiments',
    icon: '💜',
    region: 'Iɣil',
    requiredLevel: 3,
    npcName: 'Tamarguṭ (La Voyante)',
    desc: 'Joie, peine, fierté, sentiments et états d\'esprit.'
  },
  {
    id: 'histoires',
    category: 'Histoires & École',
    title: 'Histoires & Savoir',
    icon: '📚',
    region: 'Iɣil',
    requiredLevel: 3,
    npcName: 'Amacahu (Le Conteur)',
    desc: 'Contes, proverbes, éducation et mémoire des anciens.'
  },
  {
    id: 'liaison',
    category: 'Mots de liaison',
    title: 'Mots de Liaison',
    icon: '🔗',
    region: 'Iɣil',
    requiredLevel: 3,
    npcName: 'Ameskar (L\'Écrivain)',
    desc: 'Conjonctions, petits mots connecteurs et articulations de phrases.'
  },
  {
    id: 'double_pronom',
    category: 'Double Pronominalisation',
    title: 'Double Pronominalisation',
    icon: '🔀',
    region: 'Iɣil',
    requiredLevel: 3,
    npcName: 'Bab n Tajerrumt (Le Sage Grammairien)',
    desc: 'Pronoms simples COD/COI (Ḥemmleɣ-t, Fkiɣ-as, Ini-yas...) et affixes combinés (Efk-iyi-t, Fkiɣ-as-t, Ad as-t-iniɣ...).'
  },

  // Niveau 4 - Ijduden
  {
    id: 'couleurs',
    category: 'Couleurs',
    title: 'Couleurs & Teintes',
    icon: '🎨',
    region: 'Ijduden',
    requiredLevel: 4,
    npcName: 'Tazeṭṭaṭ (La Tisserande)',
    desc: 'Nuances des tapisseries traditionnelles et couleurs vives.'
  },
  {
    id: 'temps',
    category: 'Le Temps',
    title: 'Le Fil du Temps',
    icon: '⏳',
    region: 'Ijduden',
    requiredLevel: 4,
    npcName: 'Amɣar (Le Juge)',
    desc: 'Heures, saisons, jours et repères temporels.'
  }
];

export const InventoryView: React.FC<InventoryViewProps> = ({
  score, setScore, unlockedSkins, setUnlockedSkins, currentSkin, setCurrentSkin, onBack, unlockedLevels, initialTab = 'skins', onStartCelestialChallenge, isAdmin = false
}) => {
  const [activeTab, setActiveTab] = useState<'skins' | 'scrolls' | 'dialogues' | 'sms'>(() => {
    return initialTab === 'sms' ? 'sms' : initialTab === 'dialogues' ? 'dialogues' : initialTab === 'scrolls' ? 'scrolls' : 'skins';
  });

  // First-time coaching overlay for inventory rewards & unlocks
  const [showInventoryCoachingOverlay, setShowInventoryCoachingOverlay] = useState<boolean>(() => {
    try {
      return !localStorage.getItem('tamazight_inventory_hint_seen');
    } catch {
      return false;
    }
  });

  const dismissInventoryOverlay = useCallback(() => {
    setShowInventoryCoachingOverlay(false);
    try {
      localStorage.setItem('tamazight_inventory_hint_seen', 'true');
    } catch {}
  }, []);

  useEffect(() => {
    setActiveTab(initialTab === 'sms' ? 'sms' : initialTab === 'dialogues' ? 'dialogues' : initialTab === 'scrolls' ? 'scrolls' : 'skins');
  }, [initialTab]);

  // Live dictionary synchronization state
  const [vocabVersion, setVocabVersion] = useState(0);

  useEffect(() => {
    const unsubCustom = subscribeCustomWords(() => {
      setVocabVersion(v => v + 1);
    });
    const unsubOverrides = subscribeWordOverrides(() => {
      setVocabVersion(v => v + 1);
    });
    return () => {
      unsubCustom();
      unsubOverrides();
    };
  }, []);

  const customWordsList = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ver = vocabVersion;
    return getCustomWords();
  }, [vocabVersion]);

  const [readingScroll, setReadingScroll] = useState<{title: string, content: string} | null>(null);

  // Active Challenge minigame state (Unified Quiz)
  const [activeQuizChallenge, setActiveQuizChallenge] = useState<ChallengeItem | null>(null);

  // Active SMS Discussion state - Ne s'ouvre pas automatiquement au clic sur Inventaire, l'utilisateur doit être dans l'onglet SMS et cliquer
  const [activeSMSDiscussion, setActiveSMSDiscussion] = useState<SMSDiscussion | null>(null);
  const [roleSelectionDiscussion, setRoleSelectionDiscussion] = useState<SMSDiscussion | null>(null);
  const [selectedRole, setSelectedRole] = useState<'player' | 'contact'>('player');

  const [activeSavedSmsSession, setActiveSavedSmsSession] = useState<ActiveSmsSession | null>(getActiveSmsSession);
  const [activeSavedScrollSession, setActiveSavedScrollSession] = useState<ActiveScrollSession | null>(getActiveScrollSession);

  useEffect(() => {
    const syncSms = () => setActiveSavedSmsSession(getActiveSmsSession());
    const syncScroll = () => setActiveSavedScrollSession(getActiveScrollSession());
    syncSms();
    syncScroll();
    window.addEventListener('tamazight_active_sms_changed', syncSms);
    window.addEventListener('tamazight_active_scroll_changed', syncScroll);
    const onStorage = () => {
      syncSms();
      syncScroll();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('tamazight_active_sms_changed', syncSms);
      window.removeEventListener('tamazight_active_scroll_changed', syncScroll);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const [completedSMSIds, setCompletedSMSIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('tamazight_sms_discussions_completed');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const handleSMSCompleted = useCallback((discussionId: string, earnedXp: number) => {
    setScore((s) => s + earnedXp);
    setCompletedSMSIds((prev) => {
      if (!prev.includes(discussionId)) {
        const updated = [...prev, discussionId];
        try {
          localStorage.setItem('tamazight_sms_discussions_completed', JSON.stringify(updated));
        } catch {}
        return updated;
      }
      return prev;
    });
  }, [setScore]);

  // Crossword completion tracking state
  const [solvedCrosswordIds, setSolvedCrosswordIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('tamazight_crosswords_solved_grids');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const refreshSolvedCrosswords = useCallback(() => {
    try {
      const raw = localStorage.getItem('tamazight_crosswords_solved_grids');
      setSolvedCrosswordIds(raw ? JSON.parse(raw) : []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    window.addEventListener('storage', refreshSolvedCrosswords);
    window.addEventListener('tamazight_crosswords_updated', refreshSolvedCrosswords);
    return () => {
      window.removeEventListener('storage', refreshSolvedCrosswords);
      window.removeEventListener('tamazight_crosswords_updated', refreshSolvedCrosswords);
    };
  }, [refreshSolvedCrosswords]);

  // Helper to compute total and completed grids for each challenge
  const getChallengeCrosswordStats = useCallback((challenge: ChallengeItem) => {
    const words = getChallengeWords(challenge.category);
    const wordCount = words.length;
    const totalGrids = Math.max(1, Math.ceil(wordCount / MAX_WORDS_PER_GRID));
    let completedGrids = 0;

    if (totalGrids === 1) {
      if (solvedCrosswordIds.includes(`grid_challenge_${challenge.id}`)) {
        completedGrids++;
      }
    } else {
      for (let p = 1; p <= totalGrids; p++) {
        if (solvedCrosswordIds.includes(`grid_challenge_${challenge.id}_part${p}`)) {
          completedGrids++;
        }
      }
    }

    return { wordCount, totalGrids, completedGrids };
  }, [solvedCrosswordIds]);

  // Overall stats for all unlocked challenges
  const overallCrosswordStats = useMemo(() => {
    let total = 0;
    let completed = 0;
    CHALLENGES_LIST.forEach((ch) => {
      if (unlockedLevels.includes(ch.requiredLevel)) {
        const stats = getChallengeCrosswordStats(ch);
        total += stats.totalGrids;
        completed += stats.completedGrids;
      }
    });
    return { total, completed };
  }, [getChallengeCrosswordStats, unlockedLevels]);

  // Active Challenge Crossword state
  const [activeCrosswordChallenge, setActiveCrosswordChallenge] = useState<ChallengeItem | null>(null);
  const [activeCrosswordGrid, setActiveCrosswordGrid] = useState<CrosswordGrid | null>(null);
  const [activeCrosswordGrids, setActiveCrosswordGrids] = useState<CrosswordGrid[]>([]);
  const [expandedChallengeId, setExpandedChallengeId] = useState<string | null>(null);

  const handleBuy = (skinId: string, cost: number) => {
    playSound('click');
    if (score >= cost && !unlockedSkins.includes(skinId)) {
      setScore(s => s - cost);
      setUnlockedSkins(prev => [...prev, skinId]);
      setCurrentSkin(skinId);
      playSound('victory');
    }
  };

  // Start a Quiz challenge
  const startChallenge = (challenge: ChallengeItem) => {
    playSound('click');
    setActiveQuizChallenge(challenge);
  };

  // Start a Crossword challenge (dynamically generated with 100% of category keywords, chunked at 50 max)
  const startCrosswordChallenge = (challenge: ChallengeItem) => {
    playSound('click');
    const words = getChallengeWords(challenge.category);
    if (words.length === 0) return;

    const grids = generateCrosswordGridsForChallenge(
      challenge.id,
      challenge.title,
      challenge.category,
      words,
      {
        icon: challenge.icon,
        region: challenge.region,
        requiredLevel: challenge.requiredLevel,
      }
    );

    setActiveCrosswordChallenge(challenge);
    setActiveCrosswordGrids(grids);
    setActiveCrosswordGrid(grids[0] || null);
  };

  const isFullscreenView = Boolean(activeQuizChallenge || activeCrosswordGrid || activeSMSDiscussion || readingScroll);

  return (
    <div className="fixed inset-0 w-full h-[100dvh] bg-parchment text-stone-900 flex flex-col font-sans z-[10000] overflow-hidden select-none">
      {/* Top Berber Woven Rug Border */}
      <BerberRugBorder />

      {/* Header */}
      {!isFullscreenView && (
        <header className="py-2 px-3 sm:px-4 bg-[#fcf8f0] border-b border-[#dfd2be] shadow-xs flex flex-col gap-2.5 flex-shrink-0 z-30">
          {/* Topbar */}
          <div className="flex justify-between items-center w-full">
            <button 
              type="button"
              onClick={() => {
                playSound('click');
                onBack();
              }}
              className="px-3.5 py-1.5 bg-amber-900 hover:bg-amber-800 text-amber-50 font-black text-xs uppercase tracking-wider border border-amber-700 rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              <svg className="w-3 h-3 fill-amber-50 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <polygon points="17,4 7,12 17,20" />
              </svg>
              <span>Retour</span>
            </button>

            <div className="flex items-center gap-2 bg-[#ede4d4] px-3 py-1.5 rounded-full border border-[#d6c7b0] shadow-inner">
              <span className="text-[10px] text-stone-500 font-bold uppercase tracking-widest">XP</span>
              <span className="text-amber-900 font-black text-lg font-mono">{score}</span>
            </div>
          </div>

          {/* Main Tabs */}
          <div className="w-full flex p-1 gap-1 sm:gap-1.5 bg-[#ede4d4] rounded-xl border border-[#d6c7b0] shadow-inner shrink-0">
            <button 
              onClick={() => {
                playSound('click');
                setActiveTab('skins');
              }}
              className={"flex-1 py-1.5 sm:py-2 px-1 sm:px-2 text-[11px] sm:text-xs md:text-sm font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1 sm:gap-1.5 " + (activeTab === 'skins' ? 'bg-amber-800 text-white font-black shadow-xs' : 'text-stone-700 hover:text-stone-950 hover:bg-amber-100/70')}
            >
              <span>👤</span>
              <span className="truncate">Héros</span>
            </button>
            <button 
              onClick={() => {
                playSound('click');
                setActiveTab('scrolls');
              }}
              className={"flex-1 py-1.5 sm:py-2 px-1 sm:px-2 text-[11px] sm:text-xs md:text-sm font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1 sm:gap-1.5 " + (activeTab === 'scrolls' ? 'bg-amber-700 text-white font-black shadow-xs' : 'text-stone-700 hover:text-stone-950 hover:bg-amber-100/70')}
            >
              <span>📜</span>
              <span className="truncate">Contes</span>
            </button>
            <button 
              onClick={() => {
                playSound('click');
                setActiveTab('dialogues');
              }}
              className={"flex-1 py-1.5 sm:py-2 px-1 sm:px-2 text-[11px] sm:text-xs md:text-sm font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1 sm:gap-1.5 " + (activeTab === 'dialogues' ? 'bg-amber-800 text-white font-black shadow-xs' : 'text-stone-700 hover:text-stone-950 hover:bg-amber-100/70')}
            >
              <span>💬</span>
              <span className="truncate">Dialogues</span>
            </button>
            <button 
              onClick={() => {
                playSound('click');
                setActiveTab('sms');
              }}
              className={"flex-1 py-1.5 sm:py-2 px-1 sm:px-2 text-[11px] sm:text-xs md:text-sm font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1 sm:gap-1.5 " + (activeTab === 'sms' ? 'bg-amber-800 text-white font-black shadow-xs' : 'text-stone-700 hover:text-stone-950 hover:bg-amber-100/70')}
            >
              <span>📱</span>
              <span className="truncate">SMS</span>
            </button>
          </div>
        </header>
      )}

      {/* Content */}
      <div className={`flex-1 min-h-0 overscroll-contain overflow-y-auto overflow-x-hidden p-3 sm:p-4 space-y-4`}>
        
        {/* ======================================================== */}
        {/* TAB 1: SKINS / PERSONNAGES (Premier onglet / Défaut)     */}
        {/* ======================================================== */}
        {activeTab === 'skins' && (
          <div className="grid gap-3.5 max-w-2xl mx-auto">
            <div className="bg-[#fffdf9] border border-amber-400/80 rounded-xl p-3.5 text-xs text-stone-800 flex items-start gap-3 shadow-xs">
              <span className="text-xl">✨</span>
              <div className="space-y-1">
                <h4 className="font-black text-sm text-amber-900 uppercase tracking-wider">
                  Voie des Héros & Maîtrise
                </h4>
                <p className="text-[11px] text-stone-600 leading-relaxed">
                  Débloquez chaque héros par <strong>étapes successives</strong> en accumulant de l'XP à travers la quête principale et les défis thématiques.
                </p>
              </div>
            </div>

            {SKINS.map((skin, idx) => {
              const isUnlocked = unlockedSkins.includes(skin.id);
              const isSelected = currentSkin === skin.id;
              const canAfford = score >= skin.cost;
              const progressPct = skin.cost > 0 ? Math.min(100, Math.round((score / skin.cost) * 100)) : 100;
              const missingXp = Math.max(0, skin.cost - score);
              
              return (
                <div 
                  key={skin.id} 
                  className={`p-4 rounded-xl border-2 flex flex-col sm:flex-row sm:items-center gap-4 transition-all relative overflow-hidden ${
                    isSelected 
                      ? 'bg-amber-50/80 border-amber-700 shadow-sm' 
                      : isUnlocked 
                        ? 'bg-white/95 border-[#dfd2be] hover:border-amber-500 shadow-xs' 
                        : 'bg-[#f8f3eb]/90 border-[#e2d8c9] opacity-85'
                  }`}
                >
                  {/* Step indicator tag */}
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 bg-[#eadecc] rounded-xl flex items-center justify-center border border-[#d2beaa] shrink-0 relative overflow-hidden shadow-inner">
                      <div className="scale-75 translate-y-2">
                        <PlayerSprite isWalking={false} isJumping={false} skin={skin.id} />
                      </div>
                      {!isUnlocked && (
                        <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-[1px] flex items-center justify-center">
                          <span className="text-base drop-shadow">🔒</span>
                        </div>
                      )}
                    </div>

                    <div className="sm:hidden flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 uppercase">
                          {skin.tier}
                        </span>
                      </div>
                      <h3 className={`font-black text-base mt-0.5 ${isSelected ? 'text-amber-900' : 'text-stone-900'}`}>
                        {skin.name}
                      </h3>
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="hidden sm:flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 uppercase tracking-wider">
                        {skin.tier}
                      </span>
                    </div>

                    <h3 className={`hidden sm:block font-black text-lg ${isSelected ? 'text-amber-900' : 'text-stone-900'}`}>
                      {skin.name}
                    </h3>
                    <p className="text-xs text-stone-600 leading-snug mt-0.5">{skin.desc}</p>
                    
                    {/* XP Progress Bar for locked skins */}
                    {!isUnlocked && skin.cost > 0 && (
                      <div className="mt-2.5 space-y-1">
                        <div className="flex justify-between items-center text-[10px] font-mono">
                          <span className="text-stone-500">Progression d'XP</span>
                          <span className={canAfford ? 'text-emerald-700 font-bold' : 'text-amber-800 font-bold'}>
                            {score.toLocaleString()} / {skin.cost.toLocaleString()} XP ({progressPct}%)
                          </span>
                        </div>
                        <div className="w-full bg-[#ebe2d3] rounded-full h-2 overflow-hidden border border-[#d6c7b0] p-[1px]">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              canAfford 
                                ? 'bg-emerald-600' 
                                : 'bg-amber-600'
                            }`}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                        {!canAfford && (
                          <p className="text-[10px] text-stone-500 italic">
                            Encore <strong className="text-amber-800 font-semibold">{missingXp.toLocaleString()} XP</strong> nécessaires pour ce palier.
                          </p>
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex items-center gap-2">
                      {isSelected ? (
                        <span className="text-xs font-bold text-amber-900 uppercase tracking-widest bg-amber-100 border border-amber-400 px-3 py-1.5 rounded-lg shadow-2xs flex items-center gap-1.5">
                          <span>✓</span>
                          <span>Héros Actif</span>
                        </span>
                      ) : isUnlocked ? (
                        <button 
                          onClick={() => {
                            playSound('click');
                            setCurrentSkin(skin.id);
                          }}
                          className="text-xs font-bold text-white uppercase tracking-widest bg-amber-800 hover:bg-amber-900 border border-amber-700 px-4 py-2 rounded-lg transition-colors cursor-pointer active:translate-y-0.5 shadow-xs"
                        >
                          Équiper ce Héros
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleBuy(skin.id, skin.cost)}
                          disabled={!canAfford}
                          className={`text-xs font-black uppercase tracking-wider px-4 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
                            canAfford 
                              ? 'bg-amber-700 hover:bg-amber-800 text-white shadow-xs active:translate-y-0.5 cursor-pointer border border-amber-600' 
                              : 'bg-stone-200 text-stone-400 border border-stone-300 cursor-not-allowed'
                          }`}
                        >
                          <span>🔓</span>
                          <span>Débloquer le Palier ({skin.cost.toLocaleString()} XP)</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ======================================================== */}
        {/* TAB 2: SCROLLS / PARCHEMINS                              */}
        {/* ======================================================== */}
        {activeTab === 'scrolls' && (
          <div className="grid gap-3.5 max-w-2xl mx-auto w-full overflow-x-hidden">
            {/* Top Continue banner for unfinished tale */}
            {(() => {
              if (!activeSavedScrollSession) return null;
              const matchingLvl = LEVEL_DATA.find(
                (lvl) =>
                  lvl.scroll &&
                  (lvl.scroll.title === activeSavedScrollSession.title ||
                    lvl.scroll.title.trim().toLowerCase() ===
                      activeSavedScrollSession.title.trim().toLowerCase())
              );
              if (!matchingLvl?.scroll) return null;

              const cleanFr = matchingLvl.scroll.title.replace(/\s*\([^)]*\)/g, '').trim();
              const cleanKabMatch = matchingLvl.scroll.title.match(/\(([^)]+)\)/);
              const cleanKab = cleanKabMatch
                ? cleanKabMatch[1].replace(/^\s*\(|\)\s*$/g, '').trim()
                : '';

              const handleResumeScroll = () => {
                playSound('click');
                setActiveScrollSession(
                  matchingLvl.scroll!.title,
                  activeSavedScrollSession.page,
                  matchingLvl.name
                );
                setReadingScroll(matchingLvl.scroll || null);
              };

              return (
                <div
                  onClick={handleResumeScroll}
                  className="w-full bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 text-white rounded-2xl shadow-lg border-2 border-amber-400 p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-pulse-subtle cursor-pointer hover:border-amber-300 active:scale-[0.99] transition-all"
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/20 border border-white/40 flex items-center justify-center text-xl sm:text-2xl shrink-0 shadow-xs">
                      📜
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-200 bg-amber-950/60 px-2 py-0.5 rounded-md border border-amber-400/30">
                          Lecture de conte en cours
                        </span>
                        <span className="text-[11px] text-amber-200/90 font-bold">
                          Page {activeSavedScrollSession.page + 1}
                        </span>
                      </div>
                      <h4 className="text-xs sm:text-sm font-black text-white leading-snug mt-1 line-clamp-2">
                        <span>{cleanFr}</span>
                        {cleanKab && (
                          <span className="text-amber-200 font-bold ml-1 text-[11px] sm:text-xs">
                            ({cleanKab})
                          </span>
                        )}
                      </h4>
                      <p className="text-[11px] text-amber-100/80 font-medium truncate mt-0.5">
                        Sanctuaire de {matchingLvl.name}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResumeScroll();
                    }}
                    className="w-full sm:w-auto bg-white hover:bg-amber-50 text-amber-950 px-4 py-2.5 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider shadow-md hover:shadow-lg transition-all shrink-0 cursor-pointer active:scale-95 flex items-center justify-center gap-2 border border-amber-200 min-h-[42px]"
                  >
                    <span>Reprendre la lecture</span>
                    <span>➔</span>
                  </button>
                </div>
              );
            })()}

            <p className="text-xs sm:text-sm text-stone-600 mb-1 italic">
              Anciens contes kabyles retrouvés dans les sanctuaires libérés (version bilingue Français & Kabyle).
            </p>
            {LEVEL_DATA.map((lvl) => {
              const isUnlocked = unlockedLevels.includes(lvl.id) || Boolean(isAdmin);
              if (!lvl.scroll) return null;

              return (
                <div
                  key={lvl.id}
                  className={`p-3.5 sm:p-4 rounded-xl flex flex-col gap-3 w-full overflow-x-hidden ${
                    isUnlocked
                      ? 'bg-white/95 border-[#dfd2be] border shadow-xs'
                      : 'bg-[#f7f2ea]/80 border-[#e0d4c3] border opacity-70'
                  }`}
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div
                      className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-xl sm:text-2xl shrink-0 ${
                        isUnlocked
                          ? 'bg-amber-100 border border-amber-400'
                          : 'bg-stone-200 border border-stone-300 grayscale'
                      }`}
                    >
                      📜
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`font-bold text-xs sm:text-sm truncate ${
                          isUnlocked ? 'text-amber-900' : 'text-stone-400'
                        }`}
                      >
                        {isUnlocked ? lvl.scroll.title : 'Parchemin Inconnu'}
                      </h3>
                      <p className="text-[11px] sm:text-xs text-stone-500 mt-0.5">
                        Sanctuaire de {lvl.name}
                      </p>
                    </div>
                    {isUnlocked && (
                      <button
                        onClick={() => {
                          playSound('click');
                          setActiveScrollSession(lvl.scroll!.title, 0, lvl.name);
                          setReadingScroll(lvl.scroll || null);
                        }}
                        className="bg-amber-700 hover:bg-amber-800 text-white text-xs font-black uppercase tracking-wider px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-lg transition-colors cursor-pointer shadow-xs shrink-0 border border-amber-600"
                      >
                        Lire
                      </button>
                    )}
                    {!isUnlocked && (
                      <span className="text-xs font-bold text-stone-400 uppercase tracking-widest px-2 shrink-0">
                        Bloqué 🔒
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}



        {/* ======================================================== */}
        {/* TAB 3: DIALOGUES DU QUOTIDIEN EN KABYLIE                 */}
        {/* ======================================================== */}
        {activeTab === 'dialogues' && (
          <div className="grid gap-3.5 max-w-2xl mx-auto w-full overflow-x-hidden">
            {/* Top Continue banner for unfinished dialogue */}
            {(() => {
              if (!activeSavedScrollSession) return null;
              const matchingDialogue = DAILY_DIALOGUES_2026.find(
                (d) =>
                  d.scroll.title === activeSavedScrollSession.title ||
                  d.scroll.title.trim().toLowerCase() ===
                    activeSavedScrollSession.title.trim().toLowerCase()
              );
              if (!matchingDialogue) return null;

              const cleanFr = matchingDialogue.frenchTitle;
              const cleanKab = matchingDialogue.kabyleTitle;

              const handleResumeDialogue = () => {
                playSound('click');
                setActiveScrollSession(
                  matchingDialogue.scroll.title,
                  activeSavedScrollSession.page,
                  matchingDialogue.location
                );
                setReadingScroll(matchingDialogue.scroll);
              };

              return (
                <div
                  onClick={handleResumeDialogue}
                  className="w-full bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 text-white rounded-2xl shadow-lg border-2 border-amber-400 p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-pulse-subtle cursor-pointer hover:border-amber-300 active:scale-[0.99] transition-all"
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/20 border border-white/40 flex items-center justify-center text-xl sm:text-2xl shrink-0 shadow-xs">
                      {matchingDialogue.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-200 bg-amber-950/60 px-2 py-0.5 rounded-md border border-amber-400/30">
                          Dialogue en cours
                        </span>
                        <span className="text-[11px] text-amber-200/90 font-bold">
                          Page {activeSavedScrollSession.page + 1}
                        </span>
                      </div>
                      <h4 className="text-xs sm:text-sm font-black text-white leading-snug mt-1 line-clamp-2">
                        <span>{cleanFr}</span>
                        {cleanKab && (
                          <span className="text-amber-200 font-bold ml-1 text-[11px] sm:text-xs">
                            ({cleanKab})
                          </span>
                        )}
                      </h4>
                      <p className="text-[11px] text-amber-100/80 font-medium truncate mt-0.5">
                        {matchingDialogue.location}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResumeDialogue();
                    }}
                    className="w-full sm:w-auto bg-white hover:bg-amber-50 text-amber-950 px-4 py-2.5 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider shadow-md hover:shadow-lg transition-all shrink-0 cursor-pointer active:scale-95 flex items-center justify-center gap-2 border border-amber-200 min-h-[42px]"
                  >
                    <span>Reprendre le dialogue</span>
                    <span>➔</span>
                  </button>
                </div>
              );
            })()}

            <p className="text-xs sm:text-sm text-stone-600 mb-1 italic">
              Dialogues vivants de la vie quotidienne en Kabylie (Taqbaylit populaire • Version bilingue Français & Kabyle).
            </p>

            {DAILY_DIALOGUES_2026.map((dialogue) => {
              return (
                <div
                  key={dialogue.id}
                  className="p-3.5 sm:p-4 rounded-xl flex flex-col gap-3 w-full overflow-x-hidden bg-white/95 border-[#dfd2be] border shadow-xs"
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-xl sm:text-2xl shrink-0 bg-amber-100 border border-amber-400 shadow-2xs">
                      {dialogue.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-xs sm:text-sm text-amber-900 truncate">
                        {dialogue.title}
                      </h3>
                      <p className="text-[11px] sm:text-xs text-stone-600 line-clamp-1 mt-0.5">
                        {dialogue.desc}
                      </p>
                      <p className="text-[10px] text-amber-800/80 font-mono mt-0.5">
                        📍 {dialogue.location}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        playSound('click');
                        setActiveScrollSession(dialogue.scroll.title, 0, dialogue.location);
                        setReadingScroll(dialogue.scroll);
                      }}
                      className="bg-amber-700 hover:bg-amber-800 text-white text-xs font-black uppercase tracking-wider px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors cursor-pointer shadow-xs shrink-0 border border-amber-600 active:translate-y-0.5"
                    >
                      Lire
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ======================================================== */}
        {/* TAB 4: SMS DISCUSSIONS DU QUOTIDIEN                      */}
        {/* ======================================================== */}
        {activeTab === 'sms' && (
          <div className="grid gap-3.5 max-w-2xl mx-auto w-full overflow-x-hidden">
            {/* Top Continue banner for unfinished SMS */}
            {(() => {
              if (!activeSavedSmsSession) return null;
              const matchingSms = SMS_DISCUSSIONS.find(
                (d) => d.id === activeSavedSmsSession.discussionId
              );
              if (!matchingSms) return null;

              const handleResumeSms = () => {
                playSound('click');
                setSelectedRole(activeSavedSmsSession.playerRole || 'player');
                setActiveSMSDiscussion(matchingSms);
              };

              return (
                <div
                  onClick={handleResumeSms}
                  className="w-full bg-gradient-to-r from-emerald-700 via-teal-800 to-emerald-900 text-white rounded-2xl shadow-lg border-2 border-emerald-400 p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-pulse-subtle cursor-pointer hover:border-emerald-300 active:scale-[0.99] transition-all"
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/20 border border-white/40 flex items-center justify-center text-xl sm:text-2xl shrink-0 shadow-xs">
                      {matchingSms.avatar || '📱'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-widest text-emerald-200 bg-emerald-950/60 px-2 py-0.5 rounded-md border border-emerald-400/30">
                          Discussion SMS en cours
                        </span>
                        <span className="text-[11px] text-emerald-200/90 font-bold">
                          Rôle :{' '}
                          {activeSavedSmsSession.playerRole === 'contact'
                            ? matchingSms.contactName
                            : 'Vous'}
                        </span>
                      </div>
                      <h4 className="text-xs sm:text-sm font-black text-white leading-snug mt-1 line-clamp-2">
                        {matchingSms.title}
                      </h4>
                      <p className="text-[11px] text-emerald-100/80 font-medium truncate mt-0.5">
                        Avec {matchingSms.contactName}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResumeSms();
                    }}
                    className="w-full sm:w-auto bg-white hover:bg-emerald-50 text-emerald-950 px-4 py-2.5 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider shadow-md hover:shadow-lg transition-all shrink-0 cursor-pointer active:scale-95 flex items-center justify-center gap-2 border border-emerald-200 min-h-[42px]"
                  >
                    <span>Reprendre le chat</span>
                    <span>➔</span>
                  </button>
                </div>
              );
            })()}

            <div className="bg-[#fffdf9] border border-amber-400/80 rounded-xl p-3.5 text-xs text-stone-800 flex items-start gap-3 shadow-xs">
              <span className="text-2xl">📱</span>
              <div className="space-y-1">
                <h4 className="font-black text-sm text-amber-900 uppercase tracking-wider">
                  Discussions SMS du Quotidien (Tirawt)
                </h4>
                <p className="text-[11px] text-stone-600 leading-relaxed">
                  Pratiquez des échanges de la vie courante en kabyle avec traduction française intégrale. Recopiez chaque message avec le clavier virtuel ou physique pour faire défiler la discussion et remporter de l'XP !
                </p>
              </div>
            </div>

            {SMS_DISCUSSIONS.map((disc) => {
              const isCompleted = completedSMSIds.includes(disc.id);
              const playerTurnsCount = disc.messages.filter((m) => m.sender === 'player').length;
              return (
                <div
                  key={disc.id}
                  className={`p-3.5 sm:p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 w-full border transition-all ${
                    isCompleted
                      ? 'bg-[#fcfbf7] border-emerald-400/80 shadow-xs'
                      : 'bg-white/95 border-[#dfd2be] hover:border-amber-400 shadow-xs'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl border ${disc.avatarBg} shrink-0 shadow-inner relative`}>
                      <span>{disc.avatar}</span>
                      {isCompleted && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shadow-xs">
                          ✓
                        </span>
                      )}
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-black text-sm sm:text-base text-stone-900 truncate">
                          {disc.title}
                        </h3>
                      </div>
                      <p className="text-xs text-stone-700 font-medium mt-0.5">
                        Correspondant : <strong className="text-amber-950">{disc.contactName}</strong>
                      </p>
                      <p className="text-[11px] text-stone-500 leading-snug mt-0.5">
                        {disc.contextDesc}
                      </p>
                      <div className="mt-1 flex items-center gap-2 text-[10px] text-stone-500 font-mono">
                        <span>💬 {disc.messages.length} messages</span>
                        <span>•</span>
                        <span>✍️ {playerTurnsCount} répliques à recopier</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-stone-200">
                    {isCompleted ? (
                      <span className="text-[10px] font-mono font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 px-2.5 py-1 rounded-full flex items-center gap-1">
                        <span>✓</span>
                        <span>Complété (+150 XP)</span>
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2.5 py-1 rounded-full">
                        +150 XP à gagner
                      </span>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        if (activeSavedSmsSession?.discussionId === disc.id) {
                          setSelectedRole(activeSavedSmsSession.playerRole || 'player');
                          setActiveSMSDiscussion(disc);
                          return;
                        }
                        setRoleSelectionDiscussion(disc);
                      }}
                      className="bg-amber-800 hover:bg-amber-900 text-white text-xs font-black uppercase tracking-wider px-4 py-2 rounded-xl transition-all cursor-pointer shadow-xs active:translate-y-0.5 border border-amber-700 flex items-center gap-1.5"
                    >
                      <span>{activeSavedSmsSession?.discussionId === disc.id ? 'Continuer' : isCompleted ? 'Rejouer' : 'Discuter'}</span>
                      <span>➔</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* SMS Chat Screen */}
        {activeSMSDiscussion && (
          <SMSChatView
            discussion={activeSMSDiscussion}
            playerRole={selectedRole}
            onBack={() => {
              setActiveSMSDiscussion(null);
              setActiveTab('sms');
            }}
            onCompleted={handleSMSCompleted}
            isAlreadyCompleted={completedSMSIds.includes(activeSMSDiscussion.id)}
            isAdmin={Boolean(isAdmin)}
          />
        )}

        {/* Role Selection Modal */}
        <AnimatePresence>
          {roleSelectionDiscussion && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="bg-amber-50 rounded-2xl p-6 shadow-2xl max-w-sm w-full border border-amber-200 text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 border-2 border-blue-200">
                  🎭
                </div>
                <h3 className="text-xl font-black text-amber-900 mb-2 font-display">Choisissez votre rôle</h3>
                <p className="text-amber-700/80 text-sm mb-6 font-medium">
                  Quel personnage souhaitez-vous incarner dans cette discussion ? Vous recopierez ses messages.
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      playSound('click');
                      setSelectedRole('player');
                      setActiveSmsSession(roleSelectionDiscussion.id, 'player');
                      setActiveSMSDiscussion(roleSelectionDiscussion);
                      setRoleSelectionDiscussion(null);
                    }}
                    className="w-full bg-white hover:bg-amber-100 text-amber-900 border-2 border-amber-300 font-bold py-3 px-4 rounded-xl transition-colors shadow-sm flex items-center justify-between group"
                  >
                    <span>{roleSelectionDiscussion.id === 'sms-rencontre-bretagne-kabylie' ? 'Incarner Aksil' : 'Jouer votre rôle (Vous)'}</span>
                    <span className="opacity-50 group-hover:opacity-100 transition-opacity">➔</span>
                  </button>
                  <button
                    onClick={() => {
                      playSound('click');
                      setSelectedRole('contact');
                      setActiveSmsSession(roleSelectionDiscussion.id, 'contact');
                      setActiveSMSDiscussion(roleSelectionDiscussion);
                      setRoleSelectionDiscussion(null);
                    }}
                    className="w-full bg-white hover:bg-blue-50 text-blue-900 border-2 border-blue-200 font-bold py-3 px-4 rounded-xl transition-colors shadow-sm flex items-center justify-between group"
                  >
                    <span>{roleSelectionDiscussion.id === 'sms-rencontre-bretagne-kabylie' ? 'Incarner Yannick' : `Incarner ${roleSelectionDiscussion.contactName}`}</span>
                    <span className="opacity-50 group-hover:opacity-100 transition-opacity">➔</span>
                  </button>
                </div>
                <button
                  onClick={() => {
                    playSound('click');
                    setRoleSelectionDiscussion(null);
                  }}
                  className="mt-4 text-amber-600 hover:text-amber-800 text-sm font-bold uppercase tracking-wider p-2"
                >
                  Annuler
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Scroll Dual Screen Reader (Root overlay) */}
      {readingScroll && (
        <ScrollDualScreenReader
          scroll={readingScroll}
          onClose={() => setReadingScroll(null)}
          sanctuaryName={
            LEVEL_DATA.find(
              (l) =>
                l.scroll &&
                (l.scroll.title === readingScroll.title ||
                  l.scroll.title.trim().toLowerCase() === readingScroll.title.trim().toLowerCase())
            )?.name ||
            DAILY_DIALOGUES_2026.find(
              (d) =>
                d.scroll.title === readingScroll.title ||
                d.scroll.title.trim().toLowerCase() === readingScroll.title.trim().toLowerCase()
            )?.location ||
            "Kabylie"
          }
          isAdmin={isAdmin}
        />
      )}

      {/* Schematic Gesture Coaching Overlay: Inventory Rewards & Unlocks */}
      <AnimatePresence>
        {showInventoryCoachingOverlay && !isFullscreenView && (
          <motion.div
            key="inventory-coaching-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismissInventoryOverlay}
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/65 backdrop-blur-xs cursor-pointer select-none"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.84, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: -6 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
              className="bg-stone-950/90 backdrop-blur-md text-white border-2 border-amber-500/40 shadow-2xl rounded-2xl p-5 sm:p-6 flex flex-col items-center justify-center gap-3 max-w-[280px] sm:max-w-[320px] text-center transition-transform active:scale-95"
            >
              {/* Animated Schematic Visual: Trophy + Floating Stars + Bag */}
              <div className="relative w-16 h-16 flex items-center justify-center select-none">
                {/* Glowing Amber Aura */}
                <motion.div
                  animate={{ scale: [1, 1.25, 1], opacity: [0.3, 0.7, 0.3] }}
                  transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
                  className="absolute inset-0 bg-amber-500/30 rounded-full blur-md"
                />

                {/* Trophy Badge */}
                <motion.div
                  animate={{ y: [-3, 3, -3] }}
                  transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                  className="relative z-10 p-3 bg-gradient-to-br from-amber-500 to-amber-700 rounded-2xl shadow-lg border border-amber-300/60 flex items-center justify-center text-3xl"
                >
                  <span>🏆</span>
                </motion.div>

                {/* Sparkling Stars around */}
                <motion.span
                  animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.5, 1, 0.5] }}
                  transition={{ repeat: Infinity, duration: 1.4, ease: 'easeInOut' }}
                  className="absolute -top-1 -right-1 text-lg z-20 pointer-events-none"
                >
                  ✨
                </motion.span>
                <motion.span
                  animate={{ scale: [1.2, 0.8, 1.2], opacity: [0.5, 1, 0.5] }}
                  transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
                  className="absolute -bottom-1 -left-1 text-sm z-20 pointer-events-none"
                >
                  🎒
                </motion.span>
              </div>

              {/* Text Instructions */}
              <div className="flex flex-col gap-1">
                <span className="text-sm sm:text-base font-black font-cinzel text-amber-300 tracking-wide">
                  Récompenses & Déblocages
                </span>
                <p className="text-xs text-stone-300 font-sans leading-relaxed">
                  Accumulez des récompenses dans le <strong className="text-amber-400 font-bold">mode Aventure</strong> pour débloquer tous les personnages, les défis et les contes !
                </p>
              </div>

              {/* Dismiss Pill */}
              <div className="mt-1 px-3 py-1 rounded-full bg-white/10 text-[10px] sm:text-xs text-stone-300 font-mono flex items-center gap-1.5 border border-white/15">
                <span>Touchez pour continuer</span>
                <span className="text-amber-400 font-bold">✓</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
